import { useEffect, useMemo, useRef, useState } from 'react';
import '../styles/StepsSection.css';

// Vault stats per token/range combo. Replace with real numbers/API data later.
const VAULT_DATA = {
  xrp: {
    '1y': { growth: 1038, growthPct: 3.77, daysLocked: 364 },
    '5y': { growth: 1198, growthPct: 19.8, daysLocked: 1825 },
  },
  rlusd: {
    '1y': { growth: 1070, growthPct: 7.0, daysLocked: 364 },
    '5y': { growth: 1403, growthPct: 40.3, daysLocked: 1825 },
  },
};

const CHART_WIDTH = 1000;
const CHART_HEIGHT = 380;
const CHART_PADDING = 20;

/// riseFraction: how much of the chart height the line climbs (0–1).
// Kept independent of the tiny APY percentages so the line always
// reads as a bold, near-corner-to-corner diagonal like the reference.
function buildLinePoints(riseFraction) {
  const points = [];
  const startY = CHART_HEIGHT - CHART_PADDING;
  const endY = CHART_HEIGHT - CHART_PADDING - riseFraction * (CHART_HEIGHT - CHART_PADDING * 2);
  const steps = 2;
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const x = CHART_PADDING + t * (CHART_WIDTH - CHART_PADDING * 2);
    const y = startY + t * (endY - startY);
    points.push([x, y]);
  }
  return points;
}

function pointsToPath(points) {
  return points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
}

function AnimatedLine({ d, className, isVisible, delay = 0 }) {
  const pathRef = useRef(null);
  const [length, setLength] = useState(null); // null = not measured yet

  useEffect(() => {
    if (pathRef.current) {
      setLength(pathRef.current.getTotalLength());
    }
  }, [d]);

  // Don't render the stroke at all until we know its real length —
  // this stops the "flash of full line" before the grow animation starts.
  if (length === null) {
    return <path ref={pathRef} d={d} className={className} style={{ opacity: 0 }} />;
  }

  return (
    <path
      d={d}
      className={className}
      style={{
        strokeDasharray: length,
        strokeDashoffset: isVisible ? 0 : length,
        transition: `stroke-dashoffset 2s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`,
      }}
    />
  );
}

export default function StepsSection({ zIndex }) {
  const [token, setToken] = useState('xrp');
  const [range, setRange] = useState('1y');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const stats = VAULT_DATA[token][range];

  const vaultPoints = useMemo(() => buildLinePoints(0.88), []);
  const holdingPoints = useMemo(() => buildLinePoints(0.14), []);

  const vaultPath = useMemo(() => pointsToPath(vaultPoints), [vaultPoints]);
  const holdingPath = useMemo(() => pointsToPath(holdingPoints), [holdingPoints]);

  return (
    <section className="vault-growth" style={{ zIndex }} ref={sectionRef}>
      <h2 className="vault-growth__heading">
        Stop just holding—
        <br />
        Start <span className="vault-growth__accent">Earning</span> with{' '}
        <span className="vault-growth__accent">Doppler Vault</span>
      </h2>

      <div className="vault-growth__tabs">
        <button
          type="button"
          className={`vault-growth__tab ${token === 'xrp' ? 'vault-growth__tab--active' : ''}`}
          onClick={() => setToken('xrp')}
        >
          XRP
        </button>
        <button
          type="button"
          className={`vault-growth__tab ${token === 'rlusd' ? 'vault-growth__tab--active' : ''}`}
          onClick={() => setToken('rlusd')}
        >
          RLUSD
        </button>
      </div>

      <div className="vault-growth__card">
        <div className="vault-growth__card-top">
          <h3 className="vault-growth__card-title">
            Deposit 1,000 {token.toUpperCase()} on Doppler <span>vs</span> Just Holding
          </h3>
          <div className="vault-growth__range">
            <button
              type="button"
              className={`vault-growth__range-btn ${range === '1y' ? 'vault-growth__range-btn--active' : ''}`}
              onClick={() => setRange('1y')}
            >
              +1Y
            </button>
            <button
              type="button"
              className={`vault-growth__range-btn ${range === '5y' ? 'vault-growth__range-btn--active' : ''}`}
              onClick={() => setRange('5y')}
            >
              +5Y
            </button>
          </div>
        </div>

        <div className="vault-growth__stats">
          <div className="vault-growth__stat">
            <span className="vault-growth__stat-label">{token.toUpperCase()} Growth</span>
            <div className="vault-growth__stat-value">
              <span className="vault-growth__coin" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" fill="#fff" />
                  <path d="M7 7l10 10M17 7L7 17" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              {stats.growth.toLocaleString()}
              <span className="vault-growth__pct">+{stats.growthPct}%</span>
            </div>
          </div>
          <div className="vault-growth__stat">
            <span className="vault-growth__stat-label">Days Locked</span>
            <div className="vault-growth__stat-value vault-growth__stat-value--plain">
              {stats.daysLocked}
            </div>
          </div>
        </div>

        <div className="vault-growth__chart">
          <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} preserveAspectRatio="none">
            {[0.2, 0.4, 0.6, 0.8].map((f) => (
              <line
                key={f}
                x1="0"
                x2={CHART_WIDTH}
                y1={CHART_HEIGHT * f}
                y2={CHART_HEIGHT * f}
                className="vault-growth__gridline"
              />
            ))}
            <AnimatedLine
              d={holdingPath}
              className="vault-growth__line vault-growth__line--holding"
              isVisible={isVisible}
              delay={0.1}
            />
            <AnimatedLine
              d={vaultPath}
              className="vault-growth__line vault-growth__line--vault"
              isVisible={isVisible}
            />
          </svg>

          <div className="vault-growth__legend">
            <span className="vault-growth__legend-item">
              <i className="vault-growth__legend-swatch vault-growth__legend-swatch--solid" />
              XRP Vault
            </span>
            <span className="vault-growth__legend-item">
              <i className="vault-growth__legend-swatch vault-growth__legend-swatch--dashed" />
              Holding XRP
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}